import {
  PortalEventService,
  PortalEventServiceGetTicketsBonusMediaRequest,
} from "@spwn-portal/grpc";
import { useEffect, useMemo, useState } from "react";
import { useConnectClient } from "utility/connectWeb";

export type TicketIdsByEvent = {
  eventId: string;
  ticketIds: string[];
};

type Input = {
  orderId: string | null;
  /** 注文に含まれるチケットをイベントごとにまとめたもの。注文が未取得ならnull */
  ticketsByEvent: TicketIdsByEvent[] | null;
};

export const useTicketBonusMap = (input: Input) => {
  const { orderId, ticketsByEvent } = input;
  const connectClient = useConnectClient(PortalEventService);
  const [bonusMap, setBonusMap] = useState<Record<string, boolean> | null>(
    null
  );
  const [isLoadingBonus, setIsLoadingBonus] = useState(true);

  const productsByEvent = useMemo(() => {
    if (ticketsByEvent === null) return null;

    return ticketsByEvent.reduce<Record<string, string[]>>(
      (acc, { eventId, ticketIds }) => {
        if (ticketIds.length === 0) return acc;
        acc[eventId] = [...(acc[eventId] ?? []), ...ticketIds];
        return acc;
      },
      {}
    );
  }, [ticketsByEvent]);

  const hasBonusDataToFetch = useMemo(() => {
    return productsByEvent !== null && Object.keys(productsByEvent).length > 0;
  }, [productsByEvent]);

  useEffect(() => {
    if (!hasBonusDataToFetch || !productsByEvent) {
      setBonusMap(null);
      setIsLoadingBonus(false);
      return;
    }

    const fetchBonusData = async () => {
      setIsLoadingBonus(true);
      try {
        const eventIds = Object.keys(productsByEvent);
        const results = await Promise.allSettled(
          eventIds.map(async (eventId) =>
            connectClient.getTicketsBonusMedia(
              new PortalEventServiceGetTicketsBonusMediaRequest({
                eventId,
                ticketIds: productsByEvent[eventId],
                orderId: orderId ?? undefined,
              })
            )
          )
        );

        const resultMap: Record<string, boolean> = {};
        results.forEach((result) => {
          if (result.status === "fulfilled" && result.value.ticketBonusMedia) {
            result.value.ticketBonusMedia.forEach((item) => {
              resultMap[item.ticketId] = true;
            });
          }
        });
        setBonusMap(resultMap);
      } finally {
        setIsLoadingBonus(false);
      }
    };

    void fetchBonusData();
  }, [hasBonusDataToFetch, productsByEvent, connectClient, orderId]);

  return { bonusMap, isLoadingBonus };
};
